import HeaderFilter, { type TabHeaderFilter } from "./HeaderFilter";
import { tabs } from "./utils";
import StayCard from "components/StayCard/StayCard";
import { useEffect, useState } from "react";
import StayCardSkeleton from "components/StayCard/StayCardSkeleton";
import Heading from "shared/Heading/Heading";
import { useHotels } from "contexts/HotelsSearchContext/HotelProvider";

import { useMetadataUser } from "contexts/metadata-user-context";
import { EmployeeLinkModal } from "components/employee-link-modal";
import { useSearchParams } from "react-router-dom";
import { Formatter } from "utils/formatters";
import { toast } from "react-hot-toast";
import { Hero } from "components/Hero";
import ButtonPrimary from "shared/Button/ButtonPrimary";
import { LocalStorageRecentSearches } from "components/HeroSearchFormSmall/LocationInput";

export function PageHomeTemplate() {
  const {
    hotels,
    loading,
    setDestination,
    destinationName,
    messageError,
    submitSearchHotels,
    setDestinationName,
    searchHotels,
    destination,
    numNights,
    guests,
    pageReload,
    setPageReload,
    setCheckInDate,
    hotelSearchFirst,
    setHotels,
    setHotelSearchFirst,
    checkInDate,
    hiddenHero,
    getHotelSearchFirst,
    setSearchEngine,
  } = useHotels();
  const heading = "Destinos mais buscados"
  const subHeading =
    "Recomendações de destinos mais buscados por viajantes no Brasil";
  const { profile } = useMetadataUser();
  const [checkInDateHome, setCheckInDateHome] = useState<string | undefined>();
  const [searchParams] = useSearchParams();
  const employeeLink = searchParams?.get("employee_link");

  const storedDestinationsString = localStorage.getItem(
    "recentSearchesDestinations"
  );
  const storedDestinations: LocalStorageRecentSearches[] = storedDestinationsString
    ? JSON.parse(storedDestinationsString)
    : [];

  useEffect(() => {
    setSearchEngine(!hiddenHero);
  }, [hiddenHero]);

  useEffect(() => {
    if (profile) {
      const blockedUntil = profile?.subscription?.blocked_until;
      const company = profile?.company;
      const stateCheckInDate = new Date(checkInDate);
      const blockedUntilDate = blockedUntil ? new Date(blockedUntil) : null;

      if (blockedUntilDate && !company && stateCheckInDate < blockedUntilDate) {
        const setDate1Days = new Date(blockedUntil ?? "");
        setDate1Days.setDate(setDate1Days.getDate() + 2);
        setCheckInDateHome(Formatter.prettyDateHifen(setDate1Days));
        setCheckInDate(Formatter.prettyDateHifen(setDate1Days));
      } else {
        setCheckInDateHome(checkInDate);
      }
    }

    if (!pageReload && checkInDateHome) {
      searchHotels({
        destination,
        numNights,
        checkInDate: checkInDateHome ?? "",
        guests,
      }).finally(() => {
        setPageReload(false);
      });
    }
  }, [profile, pageReload, checkInDateHome]);

  const handleSearchTabs = (item: TabHeaderFilter) => {
    setDestination(item.destinationId);
    searchHotels({
      checkInDate: checkInDateHome,
      destination: item.destinationId,
    });
    setDestinationName(item.destinationCompleteName);
  };

  const renderSkeleton = () => {
    const arr = Array.from({ length: 12 }, (_, i) => i);
    return arr.map((_, i) => <StayCardSkeleton key={i} />);
  };

  const renderContent = () => {
    if (loading && submitSearchHotels) {
      return (
        <Heading desc="Localizando os melhores hotéis">Pesquisando...</Heading>
      );
    }

    if (!loading && messageError && hotels?.length === 0) {
      return (
        <div className={`
          ${hotels[0]?.destination?.name ? "flex flex-col items-start mt-12 justify-between" : "flex flex-col items-center justify-center mx-auto p-10 text-center w-full min-h-[calc(100svh-20rem)] flex-1"}
        `}>
          <Heading desc={`${hotels[0]?.destination?.name ? hotels.length + " hotéis encontrados" : ""}`}>
            {hotels[0]?.destination?.name ?? "Um problema inesperado ocorreu. Aguarde alguns minutos e tente novamente."}
          </Heading>
          {hotels[0]?.destination?.name ? null : (
            <ButtonPrimary
              onClick={() => {
                const currentTime = new Date().getTime();
                const currentDate = new Date().toDateString();
                localStorage.setItem('lastModalShownTime', JSON.stringify({ time: currentTime, date: currentDate }));
                searchHotels({
                  destination,
                  numNights,
                  checkInDate,
                  guests,
                }).finally(() => {
                  setPageReload(true);
                  const hotelSearchFirst = storedDestinations.find(
                    (item) => item.type === "HOTEL"
                  );
                  if (hotelSearchFirst) {
                    setHotelSearchFirst(getHotelSearchFirst);
                  } else {
                    setHotelSearchFirst(null);
                  }
                });
              }}
              className="bg-[--blue] hover:bg-[--blue-dark]"
            >
              Clique aqui para tentar novamente
            </ButtonPrimary>
          )}
        </div>
      );
    }

    if (hiddenHero) {
      return (
        <HeaderFilter
          tabActive={"Recife"}
          subHeading={subHeading}
          tabs={tabs}
          heading={loading ? "Buscando os melhores hotéis..." : heading}
          onClickTab={handleSearchTabs}
        />
      );
    }

    if (loading) {
      return <Heading desc="Localizando os melhores hotéis">Pesquisando...</Heading>;
    }

    return (
      <Heading desc={`${hotels[0]?.destination?.name ? hotels.length + " hotéis encontrados" : ""}`}>
        {hotels[0]?.destination?.name ?? destinationName}
      </Heading>
    );
  };

  useEffect(() => {
    if (hotelSearchFirst) {
      const hotel = hotels.find((hotel) => hotel.id === hotelSearchFirst);
      if (!hotel) {
        setHotelSearchFirst(null);
        toast.error(
          "Infelizmente esse Hotel não esta disponível nesse período, mas aqui estão outras opções incríveis."
        );
      }
    }
  }, [setHotels, hotelSearchFirst]);

  return (
    <section className="container mx-auto mt-2 mb-12">
      {hiddenHero && !pageReload && checkInDateHome && <Hero />}
      {messageError && (
        <div className="flex flex-col items-center justify-center mx-auto p-10 text-center w-full min-h-[calc(100svh-20rem)] flex-1">
          <Heading desc={messageError}>Sinto muito,</Heading>
          <ButtonPrimary
            onClick={() => {
              const currentTime = new Date().getTime();
              const currentDate = new Date().toDateString();
              localStorage.setItem('lastModalShownTime', JSON.stringify({ time: currentTime, date: currentDate }));
              window.location.reload();
            }}
            className="bg-[--blue] hover:bg-[--blue-dark]"
          >
            Clique aqui para atualizar
          </ButtonPrimary>
        </div>
      )}
      {!messageError && renderContent()}
      <div className="grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
        {hotels &&
          profile &&
          !loading &&
          !messageError &&
          hotels.reduce((acc, hotel) => {
            const hotelCard = (
              <div
                key={hotel.id}
                className={`
                ${hotelSearchFirst === hotel.id
                    ? "bg-[#4F46E5] h-full shadow-md shadow-[#4F46E5] rounded-2xl"
                    : "bg-transparent rounded-2xl"
                  }
              `}
              >
                {hotelSearchFirst === hotel.id && (
                  <p className="text-white text-sm text-center py-1">
                    Hotel da sua busca
                  </p>
                )}
                <StayCard data={hotel} profile={profile} />
              </div>
            );
            if (hotelSearchFirst === hotel.id) {
              acc.unshift(hotelCard as never);
            } else {
              acc.push(hotelCard as never);
            }
            return acc;
          }, [])}
        {loading && !messageError && renderSkeleton()}
      </div>

      {employeeLink ? <EmployeeLinkModal company_name={employeeLink} /> : null}
    </section>
  );
}
